import { Button, Space, Table, Typography, Flex } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { StyledButton } from "../../Common/Components";
import ConfirmationModal from "../../Common/Components/ConfirmationModal";
import { PaginationComponent } from "../../Common/Pagination/PaginationComponent";
import PermissionCheck from "../../Common/PermissionCheck";
import { useDeleteConference, useFetchConferences } from "../../entities/api";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useGetOrgData } from "../../hooks/useGetOrgData";
import { useHandlePagination } from "../../hooks/useHandlePagination";

const PermissionName = PermissionNames.CONFERENCE;
const columnsone = ({ customerId, sport, organizationId, formatMessage, handleOpenConfirmation }) => {
  return [
    {
      title: formatMessage({
        id: "name.local.full",
        defaultMessage: "Local Full Name",
      }),
      dataIndex: "nameLocal",
      width: "70px",
    },
    {
      title: formatMessage({
        id: "name.local.abbreviation",
        defaultMessage: "Local Abbr",
      }),
      dataIndex: "abbreviationLocal",
      width: "70px",
    },
    {
      render: (_, record) => (
        <div style={{ textAlign: "right" }}>
          <PermissionCheck permissionKeys={[PermissionNames.DIVISION]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/conferences/${record.conferenceId}/divisions`}>
              <StyledButton type={"text"}>
                {formatMessage({
                  id: "divisions",
                  defaultMessage: "Divisions",
                })}
              </StyledButton>
            </Link>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.UPDATE]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/conferences/manage/${record.conferenceId}/`}>
              <StyledButton type={"text"}>
                {formatMessage({
                  id: "manage",
                  defaultMessage: "Manage",
                })}
              </StyledButton>
            </Link>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.DELETE]}>
            <StyledButton
              type={"text"}
              onClick={() => {
                handleOpenConfirmation(record);
              }}
            >
              <i data-testid="delete-icon" className={`fas fa-trash-can`} />
            </StyledButton>
          </PermissionCheck>
        </div>
      ),
      width: 1,
    },
  ];
};

const ConferencesList = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId, customerId } = useGetOrgData();
  const [selected, setSelected] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const { mutate: deleteFn, isPending } = useDeleteConference();

  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination();
  const { data: confData, isLoading } = useFetchConferences({
    limit: pageSize,
    offset,
  });

  const data = useMemo(() => confData?.data || [], [confData?.data]);
  const nextLink = useMemo(() => confData?.links?.next, [confData?.links?.next]);

  useEffect(() => {
    setFilteredData(data);
  }, [data, setFilteredData]);

  const handleOpenConfirmation = useCallback(
    (record) => {
      setSelected(record);
      setShowConfirmation(true);
    },
    [setSelected, setShowConfirmation],
  );

  const handleDelete = useCallback(() => {
    deleteFn(selected?.conferenceId);
  }, [deleteFn, selected]);

  return (
    <Flex vertical gap={8}>
      <Flex align="center" justify="space-between">
        <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.DELETE]}>
          <ConfirmationModal
            title="Delete Competition"
            setIsModalOpen={setShowConfirmation}
            isModalOpen={showConfirmation}
            handleOk={handleDelete}
            handleCancel={() => {
              setSelected({});
            }}
            okText="Delete"
          >
            <div>{`You are about to delete ${selected.nameLocal}. Is this OK?`}</div>
          </ConfirmationModal>
        </PermissionCheck>
        <Typography.Title level={4}>Conferences</Typography.Title>
        <Space>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.CREATE]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/conferences/manage/`}>
              <Button type="primary" icon={<i className="fa-solid fa-plus mr-1"></i>}>
                Conference
              </Button>
            </Link>
          </PermissionCheck>
        </Space>
      </Flex>
      <Table
        loading={isPending || isLoading}
        size={"small"}
        columns={columnsone({
          customerId,
          sport,
          organizationId,
          formatMessage,
          handleOpenConfirmation,
        })}
        rowKey={"conferenceId"}
        pagination={{
          current: currentPage,
          defaultPageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions,
          showQuickJumper: false,
          total: nextLink ? offset + data.length + 1 : data.length + offset,
          position: ["bottomCenter"],
          onChange: handlePaginationChange,
          showTotal: (total, range) => (
            <PaginationComponent pageNumber={currentPage} pageSize={pageSize} total={total} range={range} />
          ),
        }}
        dataSource={filteredData}
      />
    </Flex>
  );
};

export default ConferencesList;
